import { Clock, MapPin, Navigation, Fuel, ShipWheel } from "lucide-react";

const CurrentVoyageData = ({
  voyageData = {},
  selectedEngine,
  totalFuelConsumptionMT,
}) => {
  const voyage = voyageData || {};
  console.log("Voyage:", voyage);
  console.log("enginerData:", selectedEngine);
  const route = {
    from: voyage.fromPort || "Unknown",
    to: voyage.toPort || "Unknown",
    totalDistance: voyage.totalDistance || 0,
    traveledDistance: voyage.distanceTravelled || 0,
  };

  const fuel = {
    consumed: totalFuelConsumptionMT || 0,
    budget: 500, // This would ideally come from API or config
  };

  const progressPercentage =
    route.totalDistance > 0
      ? (route.traveledDistance / route.totalDistance) * 100
      : 0;

  const fuelPercentage = Math.min((fuel.consumed / fuel.budget) * 100, 100);
  const isOverBudget = fuel.consumed > fuel.budget;
  const fuelOverage = isOverBudget ? fuel.consumed - fuel.budget : 0;

  const lastUpdated = voyage.updatedAt
    ? new Date(voyage.updatedAt).toUTCString().replace("GMT", "UTC")
    : "No data available";

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-6 pt-5 pb-3 border-b border-gray-100 flex items-center gap-3">
        <div className="p-1.5 bg-[#e1f5fb] text-[#27ade3] rounded-md">
          <ShipWheel className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <h2 className="text-lg font-semibold text-[#0a2540] tracking-tight">
          Current Voyage Data{" "}
          {selectedEngine && `- ${selectedEngine.toUpperCase()}`}
        </h2>
      </div>

      <div className="p-6">
        {/* Route Section */}
        <RouteVisualization
          from={route.from}
          to={route.to}
          traveledDistance={route.traveledDistance}
          totalDistance={route.totalDistance}
          progressPercentage={progressPercentage}
        />

        {/* Fuel Section */}
        <FuelConsumption
          consumed={fuel.consumed}
          budget={fuel.budget}
          percentage={fuelPercentage}
          isOverBudget={isOverBudget}
          overage={fuelOverage}
        />

        {/* Last Updated */}
        <LastUpdated timestamp={lastUpdated} />
      </div>

      <style jsx global>{`
        @keyframes lightStreak {
          0% {
            transform: translateX(-100%) skewX(-20deg);
          }
          100% {
            transform: translateX(100%) skewX(-20deg);
          }
        }
      `}</style>
    </div>
  );
};

const RouteVisualization = ({
  from,
  to,
  traveledDistance,
  totalDistance,
  progressPercentage,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
    {/* Route */}
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center text-green-600">
        <MapPin className="w-4 h-4 mr-2" />
        <div>
          <div className="text-xs text-gray-500 font-medium">FROM</div>
          <div className="text-green-700 font-semibold">{from}</div>
        </div>
      </div>

      <Navigation className="w-5 h-5 text-gray-400 transform rotate-90" />

      <div className="flex items-center text-purple-600">
        <MapPin className="w-4 h-4 mr-2" />
        <div>
          <div className="text-xs text-gray-500 font-medium">TO</div>
          <div className="text-purple-700 font-semibold">{to}</div>
        </div>
      </div>
    </div>

    {/* Distance */}
    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
      <div className="flex items-center text-[#1a5276]">
        <Navigation className="w-5 h-5 mr-2" />
        <span className="font-semibold">Distance Sailed</span>
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-[#1a5276]">
          {traveledDistance.toFixed(2)} NM
        </div>
        <div className="text-sm text-gray-500">
          Total: {totalDistance.toFixed(2)} NM
        </div>
      </div>
    </div>
  </div>
);

const FuelConsumption = ({
  consumed,
  budget,
  percentage,
  isOverBudget,
  overage,
}) => (
  <div className="mb-6">
    <div className="flex justify-between items-center mb-1">
      <div className="flex items-center text-[#d35400]">
        <Fuel className="w-4 h-4 mr-1" />
        <span className="text-sm font-medium">ME Consumption</span>
      </div>
      <div
        className={`text-sm font-bold ${
          isOverBudget ? "text-[#7d3c98]" : "text-[#d35400]"
        }`}
      >
        {consumed.toFixed(2)} MT
        {isOverBudget && (
          <span className="ml-1 text-xs font-normal text-red-500">
            (+{overage.toFixed(2)} MT)
          </span>
        )}
      </div>
    </div>

    <ProgressBar
      percentage={percentage}
      gradient={
        isOverBudget
          ? "from-amber-400 to-red-500"
          : "from-amber-300 to-amber-500"
      }
      isOverBudget={isOverBudget}
      overagePercentage={isOverBudget ? (overage / budget) * 100 : 0}
    />

    <div className="flex justify-between text-xs text-gray-500 mt-1">
      <span>Maximum: {budget} MT</span>
    </div>
  </div>
);

const ProgressBar = ({
  percentage,
  gradient,
  isOverBudget = false,
  overagePercentage = 0,
}) => (
  <div className="relative h-2.5 bg-gray-200 rounded-full overflow-hidden mb-1">
    <div
      className={`absolute h-full bg-gradient-to-r ${gradient} rounded-full`}
      style={{ width: `${isOverBudget ? 100 : percentage}%` }}
    >
      <div
        className="absolute inset-0 bg-white/20"
        style={{
          width: "50%",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)",
          transform: "skewX(-20deg)",
          animation: "lightStreak 2.5s ease-in-out infinite",
        }}
      ></div>
    </div>
    {isOverBudget && (
      <div
        className="absolute h-full bg-red-500 rounded-r-full"
        style={{
          width: `${overagePercentage}%`,
          left: "100%",
        }}
      ></div>
    )}
  </div>
);

const LastUpdated = ({ timestamp }) => (
  <div className="flex items-center justify-center p-3 bg-blue-50 rounded-lg border border-blue-100">
    <Clock className="w-4 h-4 text-blue-500 mr-2" />
    <span className="text-sm font-medium text-[#1a5276]">
      Last updated (UTC Time): {timestamp}
    </span>
  </div>
);

export default CurrentVoyageData;
